'use client';

import { FC, useMemo, useState } from 'react';
import { Content, isFilled, LinkField } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { SliceComponentProps } from '@prismicio/react';
import DynamicMarquee from '@/components/DynamicMarquee';
import PageHeroRedesign from '@/components/redesign/PageHeroRedesign';

/**
 * Props for `CaseHeroHighlight`.
 */
export type CaseHeroHighlightProps =
  SliceComponentProps<Content.CaseHeroHighlightSlice>;

/**
 * Subtle edge fade for the logo ribbon — matches the HeroVelxEngine redesign
 * variant so both heroes share the same marquee visual.
 */
const LOGO_RIBBON_MASK: React.CSSProperties = {
  WebkitMaskImage:
    'linear-gradient(to right, transparent 0, black 10px, black calc(100% - 10px), transparent 100%)',
  maskImage:
    'linear-gradient(to right, transparent 0, black 10px, black calc(100% - 10px), transparent 100%)',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
};

// A single pass of the unique logos must be at least as wide as the visible
// strip so no logo shows twice at once. See HeroVelxEngine redesign for the
// original derivation.
const LOGO_RIBBON_TARGET_WIDTH = 1320;
const LOGO_CELL_MIN_WIDTH = 160;

// Default label when the slice's `cta` field has neither a URL nor a text
// override. The synthetic Web link below points at the shared Get Started
// modal via `#modal-get-started`, which `useModalLinkDispatcher` intercepts —
// same path the navbar's Get Started button uses, so both entry points open
// the exact same modal.
const DEFAULT_MODAL_CTA_LABEL = 'Book a Technical Demo';

const buildModalCtaLink = (label: string): LinkField =>
  ({
    link_type: 'Web',
    url: '#modal-get-started',
    text: label,
  }) as LinkField;

// Stronger variant of the shared PageHeroRedesign bottom overlay: the ramp
// starts higher (~28%) so the darkening is visibly present from the logo
// down, but the peak opacities stay the same as before — this is a shift of
// where the gradient falls, not a deeper color.
const CASE_HERO_BOTTOM_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 28%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.85) 100%), linear-gradient(180deg, rgba(5,242,115,0) 0%, rgba(5,242,115,0.12) 28%, rgba(5,242,115,0.3) 45%, rgba(5,242,115,0.5) 100%)';

/**
 * Component for "CaseHeroHighlight" Slices.
 */
const CaseHeroHighlight: FC<CaseHeroHighlightProps> = ({ slice }) => {
  const {
    background_image,
    company_logo,
    headline,
    description,
    cta,
    logos_title,
    logos,
  } = slice.primary;

  // When the editor sets a real URL on `cta`, honor it as-is (its own `text`
  // becomes the button label via OutlineCtaLink). When only the CTA text is
  // set — a common shorthand for "I want this button to open the modal but
  // relabelled" — carry that text into the synthetic modal link. If neither
  // URL nor text is set, fall back to the default label.
  const authoredCtaText = (cta as { text?: string } | undefined)?.text;
  const ctaField = isFilled.link(cta)
    ? cta
    : buildModalCtaLink(
        authoredCtaText && authoredCtaText.trim().length > 0
          ? authoredCtaText
          : DEFAULT_MODAL_CTA_LABEL,
      );

  const [isPaused, setIsPaused] = useState(false);
  const logoItems = useMemo(
    () => (logos ?? []).filter((item) => isFilled.image(item.logo)),
    [logos],
  );
  const doubledLogos = useMemo(
    () => (logoItems.length > 0 ? [...logoItems, ...logoItems] : []),
    [logoItems],
  );
  const logoCellMinWidth =
    logoItems.length > 0
      ? Math.max(
          LOGO_CELL_MIN_WIDTH,
          Math.ceil(LOGO_RIBBON_TARGET_WIDTH / logoItems.length),
        )
      : LOGO_CELL_MIN_WIDTH;

  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <PageHeroRedesign
        logo={company_logo}
        logoClassName="origin-top-left scale-[1.3]"
        title={headline}
        description={description}
        ctaLink={ctaField}
        backgroundImage={background_image}
        applyBottomGradient
        bottomGradientOverlay={CASE_HERO_BOTTOM_GRADIENT}
      >
        {logoItems.length > 0 && (
          <div className="relative bg-[#262626] px-5 py-8 md:px-[30px] lg:py-[30px]">
            <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start gap-8 lg:gap-[35px]">
              {logos_title && (
                <p className="w-full text-lg font-medium leading-[1.4] text-[#aaaaaa] md:text-[26px] lg:text-[30px]">
                  {logos_title}
                </p>
              )}
              <div className="relative w-full" style={LOGO_RIBBON_MASK}>
                <DynamicMarquee speed={30} gradient={false} play={!isPaused}>
                  {doubledLogos.map((item, index) => {
                    const logoImage = (
                      <PrismicNextImage
                        field={item.logo}
                        className="h-[28px] w-auto object-contain opacity-70 transition-opacity duration-200 hover:opacity-100"
                        fallbackAlt=""
                      />
                    );

                    return (
                      <div
                        key={`logo-${index}`}
                        className="flex h-[52px] shrink-0 items-center justify-center px-8"
                        style={{ minWidth: `${logoCellMinWidth}px` }}
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                      >
                        {isFilled.link(item.logo_link) ? (
                          <PrismicNextLink
                            field={item.logo_link}
                            aria-label="View client case study"
                          >
                            {logoImage}
                          </PrismicNextLink>
                        ) : (
                          logoImage
                        )}
                      </div>
                    );
                  })}
                </DynamicMarquee>
              </div>
            </div>
          </div>
        )}
      </PageHeroRedesign>
    </div>
  );
};

export default CaseHeroHighlight;
